<template>
  <section class="text-mode-layout">
    <div class="text-mode-layout__grid">
      <section class="text-mode-layout__main">
        <ClinicalNotePanel
            :clinical-note="clinicalNote"
            :blocked-by-language-alert="blockedByLanguageAlert"
            :show-language-reminder="showLanguageReminder"
            :language-notice-message="languageNoticeMessage"
            @note-input="handleNoteInput"
            @note-submit="handleNoteSubmit"
            @note-resubmit="handleNoteResubmit"
            @mode-change="handleModeChange"
        />

        <QuestionsPanel
            v-if="!dangerSignHold && questions && questions.length > 0"
            :questions="questions"
            :stage="questionStage"
            :has-network-error="hasNetworkSubmitError"
            :is-submitting="clinicalNote.isSubmitting"
            @toggle="handleQuestionToggle"
            @answer="handleQuestionAnswer"
            @submit="handleQuestionSubmit"
        />

        <div
            v-else-if="!dangerSignHold && questions && questions.length === 0 && hasObservations()"
            class="completed-questions-section"
        >
          <FocusedCompletionPrompt
              v-if="showFocusedCompletionPrompt"
              :disabled="clinicalNote.isSubmitting"
              :complete-disabled="patientIdBlocksCompletion"
              @complete="handleComplete"
              @continue="handleFocusedContinue"
              @back="handleFocusedBack"
          />
          <CompletedQuestionsCard v-else />
          <button
              v-if="!showFocusedCompletionPrompt"
              type="button"
              data-analytics-skip
              class="text-mode-layout__complete text-mode-layout__complete--completed-questions"
              :disabled="!canComplete || patientIdBlocksCompletion"
              @click="handleComplete"
          >
            {{ $t('app.assessment_mode.text.input.complete_assessment') }}
          </button>
        </div>
      </section>

      <aside class="text-mode-layout__side">
        <p
          v-if="isComprehensiveScope"
          class="text-mode-layout__assessment-scope"
          :class="{ 'text-mode-layout__assessment-scope--black': skinTone === 'black' }"
        >
          {{ $t('app.assessment_mode.summary.full_systems_assessment') }}
        </p>
        <EvidencePanel
            :categories="evidenceCategories"
            @revert-item="handleRevertEvidenceItem"
            @rhs-edit-start="handleRhsEditStarted"
            @rhs-submit="handleRhsSubmit"
        />
        <div v-if="allowCopyPaste" class="text-mode-layout__summary-copy">
          <copy-clipboard-button
            token="app.assessment.summary.button.copy.title"
            name="summary"
            css-class="copy-all-btn"
            :payload="getCopyPayload"
            mode="button"
          />
        </div>
      </aside>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, PropType } from 'vue';
import { mapState } from 'pinia';
import ClinicalNotePanel from './ClinicalNotePanel.vue';
import QuestionsPanel from './Questions/QuestionsPanel.vue';
import CompletedQuestionsCard from './Questions/CompletedQuestionsCard.vue';
import FocusedCompletionPrompt from './Questions/FocusedCompletionPrompt.vue';
import EvidencePanel from './Evidence/EvidencePanel.vue';
import CopyClipboardButton from '@/components/widgets/CopyClipboardButton.vue';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useTrackStore } from '@/stores/track';
import { useDraftStore } from '../stores/draft.store';
import { usePatientStore } from '../stores/patient.store';
import { usePatientIdValidation } from '../composables/usePatientIdValidation';
import { buildEvidenceSummaryCopyPayload } from '@/utils/encounterCopyUtils';
import type {
  ClinicalNoteState,
  QuestionItem,
  EvidenceCategory,
  QuestionStage,
} from '../types/text-mode.types';

export default defineComponent({
  name: 'TextModeLayout',

  components: {
    ClinicalNotePanel,
    QuestionsPanel,
    CompletedQuestionsCard,
    FocusedCompletionPrompt,
    EvidencePanel,
    CopyClipboardButton,
  },

  props: {
    clinicalNote: {
      type: Object as PropType<ClinicalNoteState>,
      required: true,
    },

    questions: {
      type: Array as PropType<QuestionItem[]>,
      required: true,
    },

    questionStage: {
      type: String as PropType<QuestionStage>,
      default: undefined,
    },

    evidenceCategories: {
      type: Array as PropType<EvidenceCategory[]>,
      required: true,
    },

    canComplete: {
      type: Boolean,
      default: false,
    },

    showFocusedCompletionPrompt: {
      type: Boolean,
      default: false,
    },

    dangerSignHold: {
      type: Boolean,
      default: false,
    },

    hasNetworkSubmitError: {
      type: Boolean,
      default: false,
    },

    blockedByLanguageAlert: {
      type: Boolean,
      default: false,
    },

    showLanguageReminder: {
      type: Boolean,
      default: false,
    },

    languageNoticeMessage: {
      type: String,
      default: '',
    },
  },

  setup(props, { emit }) {
    const vm = getCurrentInstance();
    const draftStore = useDraftStore();
    const patientStore = usePatientStore();
    const { patientIdBlocksCompletion } = usePatientIdValidation();

    const hasObservations = (): boolean => {
      return props.evidenceCategories
        .flatMap(cat => cat.items)
        .length > 0;
    };

    const handleNoteInput = (value: string): void => {
      emit('note-input', value);
    };

    const handleNoteSubmit = (value: string): void => {
      emit('note-submit', value);
    };

    const handleQuestionToggle = (questionId: string): void => {
      emit('question-toggle', questionId);
    };

    const handleQuestionAnswer = (payload: { questionId: string; answer: string }): void => {
      emit('question-answer', payload);
    };

    const handleQuestionSubmit = (questions: QuestionItem[]): void => {
      emit('question-submit', questions);
    };

    const handleRevertEvidenceItem = (payload: { itemId: string }): void => {
      emit('revert-evidence-item', payload);
    };

    const handleRhsSubmit = (): void => {
      emit('rhs-submit');
    };

    const handleRhsEditStarted = (): void => {
      emit('rhs-edit-start');
    };

    const handleNoteResubmit = (): void => {
      emit('note-resubmit');
    };


    const handleModeChange = (mode: string): void => {
      emit('mode-change', mode);
    };

    const handleComplete = (): void => {
      emit('complete');
    };

    const handleFocusedContinue = (): void => {
      emit('focused-continue');
    };

    const handleFocusedBack = (): void => {
      emit('focused-back');
    };

    const getCopyPayload = (): string => buildEvidenceSummaryCopyPayload({
      identifiers: [{
        label: String(vm?.proxy.$t('app.assessment_mode.text.input.patient_id_label')),
        value: patientStore.patient?.id,
      }],
      categories: props.evidenceCategories,
      questions: props.questions,
      patientId: patientStore.patient?.id,
      state: {
        draftOverrides: draftStore.draftOverrides,
        committedValues: draftStore.committed.values,
        committedIncorrectIds: draftStore.committed.incorrectIds,
        committedObservationStatuses: draftStore.committed.observationStatuses,
      },
      translate: token => String(vm?.proxy.$t(token) ?? token),
      formatNumber: value => String(vm?.proxy.$n(value, 'decimal') ?? value),
      rtl: Boolean(vm?.proxy.$vuetify.rtl),
    });

    return {
      handleNoteInput,
      handleNoteSubmit,
      handleNoteResubmit,
      handleModeChange,
      handleQuestionToggle,
      handleQuestionAnswer,
      handleQuestionSubmit,
      handleRevertEvidenceItem,
      handleRhsEditStarted,
      handleRhsSubmit,
      handleComplete,
      handleFocusedContinue,
      handleFocusedBack,
      hasObservations,
      getCopyPayload,
      patientIdBlocksCompletion,
    };
  },

  computed: {
    ...mapState(usePartnerConfigStore, ['allowCopyPaste', 'skinTone']),
    ...mapState(useTrackStore, ['isComprehensiveScope']),
  },
});
</script>
